/* eslint-disable max-lines */
// internal-deps
import { format, isEmpty } from "@swiggy-private/js-utils";
import { CollectionProps, GridLayout, Style, IDiscount } from "@swiggy-private/checkout-types";
import { IColors, ISpacing, ITypography } from "@swiggy-private/dls-web-common/dist/components/types.internal";

// interfaces
import {
    IBillEntries,
    IBillItem,
    IGetAllBillEntriesProps,
    IOfferLogoStyle,
} from "@dineout-payment-success-ui/interfaces";

// constants
import {
    BILL_ELEMENT_ENTRIES,
    CAROUSEL_DIMENTIONS,
    CART_BILL_ENTRY_ONE_LOGO_STYLE,
    DealCategory,
    PAYMENT_SUCCESS_ONE_LOGO_STYLE,
    REDEEM_TEXT,
    TEXT_TYPOGRAPHY,
} from "@dineout-payment-success-ui/constants/Constants";
import { ONE_PLAN_LOGO_PROPS, OnePlanType } from "@dineout-payment-success-ui/constants";
import { isStealStoreFlow } from "../helpers";

let windowWidth: number;
let windowHeight: number;
if (typeof window !== "undefined") {
    windowWidth = window.innerWidth;
    windowHeight = window.innerHeight;
} else {
    windowHeight = 0;
    windowWidth = 0; // Or any default value you prefer
}

export const getWidthHeightStyles = (
    defaultStyle: { width: number; height: number },
    style?: Style,
): { width: number; height: number } => {
    const styles = defaultStyle;

    if (style && style.height) {
        const h = (style.height.value as number) || 1;
        if (style.height.type === "TYPE_ABSOLUTE") {
            styles.height = h;
        } else if (style.height.reference === "RELATIVE_DIMENSION_REFERENCE_WIDTH") {
            styles.height = h * windowWidth;
        } else {
            styles.height = h * windowHeight;
        }
    }

    if (style?.width && style?.width.value) {
        const w = (style.width.value as number) || 1;
        styles.width = style.width.type === "TYPE_ABSOLUTE" ? w : w * windowWidth;
    }
    return styles;
};

export const appendString = (a: string, b: string, c = "") => {
    if (!b) {
        return b;
    }
    const includesA = a ? b.includes(a) : false;
    const includesC = c ? b.includes(c) : false;
    if (includesA || includesC) {
        return b;
    }
    return `${a ? `${a}, ` : ""}${b}${c ? `, ${c}` : ""}`;
};
export const isInvalidTip = (parsedVal: string): boolean => {
    return parsedVal.startsWith("0");
};
/**
 * Formats a given number into a string with comma separation for thousands, lakhs and supports optional decimal places.
 *
 * @param {number} num The number to format.
 * @param {number} [decimalUnit=0] The number of decimal places to include in the formatted string. Defaults to 0 if not specified.
 * @returns {string} The formatted string with comma-separated thousands and optional decimal places.
 */

export const currencyFormat = (num: number, decimalUnit?: number): string => {
    return num?.toFixed?.(decimalUnit ? decimalUnit : 0).replace(/\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g, ",");
};

export const convertCommaSeparatedStringToNumber = (text: string): number => {
    if (!text) return 0;
    return Number(text.replace(/,/g, ""));
};

interface ILogoStyle {
    width: number;
    height: number;
}

export interface ISuccessLogoStyle {
    successAnimationOneStyle: ILogoStyle;
    paymentScreenOneStyle: ILogoStyle;
}

export const getPaymentSuccessOneLogoStyle = (): ISuccessLogoStyle => {
    return PAYMENT_SUCCESS_ONE_LOGO_STYLE.DEFAULT;
};

export const getBillEntryOneLogoStyle = (): ILogoStyle => {
    return CART_BILL_ENTRY_ONE_LOGO_STYLE.DEFAULT;
};

export const getFormattedDiscountText = (value: number, originalValue: string): string | null => {
    return value > 0
        ? `${BILL_ELEMENT_ENTRIES.MINUS_SYMBOL}${BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL}${originalValue}`
        : null;
};
const processDiscountList = (discountList?: IDiscount[], isCart = false) => {
    const billEntries: Array<IBillEntries> = [];
    discountList?.forEach((discount: IDiscount, index: number) => {
        if (discount?.couponType !== BILL_ELEMENT_ENTRIES.DISCOUNT_TYPE_FLAT) {
            const offerTitle = `${discount?.ctx?.offerTitle ? discount?.ctx?.offerTitle : ""}`;
            const offerDescription = `${discount?.description ? discount.description : ""}`;
            const valueOfLeftText = isCart ? offerDescription : `${offerTitle} ${offerDescription}`;

            const value = convertCommaSeparatedStringToNumber(discount?.value);
            if (valueOfLeftText.trim().length) {
                billEntries.push({
                    id: BILL_ELEMENT_ENTRIES.DISCOUNT_ID + index,
                    leftElement: { text: valueOfLeftText.trimStart() },
                    rightElement: {
                        text: getFormattedDiscountText(value, discount?.value),
                        color: "positive",
                        variant: "V2.body2.neutral.medium",
                    },
                    offerLogo: isCart ? "" : discount?.ctx?.offerLogo, /// currently in prod we are not showing this logo
                    offerLogoStyle: discount?.ctx?.offerLogo ? getBillEntryOneLogoStyle() : {},
                    offerName: discount?.ctx?.offerName,
                    superOffer: discount?.superOffer,
                    isBillDivider: false,
                });
            }
        }
    });
    const billDataLength = billEntries?.length;
    if (isCart && billDataLength > 0) {
        billEntries[billDataLength - 1] = { ...billEntries[billDataLength - 1], isBillDivider: true };
    }
    return billEntries;
};
const findBillDivider = (billEntries: Array<IBillEntries> = []) => {
    let isBillDivider = false;
    const billEntriesLength = billEntries?.length;
    if (billEntriesLength > 0 && billEntries?.[billEntriesLength]?.id !== BILL_ELEMENT_ENTRIES.TOTAL_BILL_AMOUNT_ID) {
        billEntries[billEntriesLength - 1] = { ...billEntries?.[billEntriesLength - 1], isBillDivider: false };
        isBillDivider = true;
    }
    return { billEntries, isBillDivider };
};
export const getAllBillEntries = ({
    discountList,
    subscription,
    feeObject,
    totalBillAmount,
    toPayAmount,
    tip,
    advanceAmount = 0,
    enteredBillAmount = 0,
    isTableReservation,
    guestCount,
    isCart,
    redeemable,
    billItems,
    dealCategory,
    dineCash,
    gst,
    partialPaymentDetails,
}: IGetAllBillEntriesProps) => {
    let billEntries = [];
    const isEventCart = dealCategory === DealCategory.EVENTS;

    const isStealStoreOrder = isStealStoreFlow(dealCategory);

    const getRedeemableSubText = () => {
        return redeemable && isTableReservation
            ? {
                  leftSubText: REDEEM_TEXT,
                  leftSubTextStyles: {
                      variant: "V2.overline.neutral.regular" as keyof ITypography,
                      color: "positive" as keyof IColors,
                  },
              }
            : {};
    };

    const getTotalAmountText = () => (isCart ? BILL_ELEMENT_ENTRIES.AMOUNT_TO_PAY : BILL_ELEMENT_ENTRIES.TOTAL_PAID);

    const getLeftTextForBillEntry = (id: string): string => {
        const isPartialPayment = partialPaymentDetails?.isPartialPayment;
        const BILL_PAID = isTableReservation ? getTotalAmountText() : BILL_ELEMENT_ENTRIES.AMOUNT_PAID;
        switch (id) {
            case BILL_ELEMENT_ENTRIES.TOTAL_BILL_AMOUNT_ID:
                if (isTableReservation) {
                    return format(BILL_ELEMENT_ENTRIES.PRE_BOOK_TOTAL_BILL_AMOUNT, guestCount);
                } else if (isStealStoreOrder) {
                    return BILL_ELEMENT_ENTRIES.ITEM_TOTAL;
                }
                return BILL_ELEMENT_ENTRIES.TOTAL_BILL_AMOUNT;
            case BILL_ELEMENT_ENTRIES.BILL_TO_PAY_ID:
                if (isCart) {
                    return BILL_ELEMENT_ENTRIES.TO_PAY;
                }
                return isPartialPayment ? BILL_ELEMENT_ENTRIES.TOTAL_BILL : BILL_PAID;
            default:
                return "";
        }
    };

    const getBillAmount = () =>
        BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL +
        (!isEmpty(enteredBillAmount) ? currencyFormat(enteredBillAmount) : currencyFormat(totalBillAmount));

    const getSubscriptionStrikedAmount = () =>
        Number(subscription?.basePrice || "0") !== Number(subscription?.finalPrice || "0")
            ? BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + subscription?.basePrice
            : "";

    const getOfferUrlAndStyle = (): { offerLogo?: string; offerLogoStyle?: IOfferLogoStyle } => {
        if (!isTableReservation) return {};
        const planType = String(subscription?.planType || "").toUpperCase();
        const resolvedType = planType === OnePlanType.PLUS ? OnePlanType.PLUS : OnePlanType.LITE;
        const planConfig = ONE_PLAN_LOGO_PROPS[resolvedType];
        const offerLogo = planConfig?.source;
        const offerLogoStyle = planConfig?.imageStyle as IOfferLogoStyle;
        return { offerLogo, offerLogoStyle };
    };

    billItems?.map((item: IBillItem, index: number) => {
        billEntries.push({
            id: `${item.id}-${index}`,
            leftElement: {
                text: item.leftText,
            },
            rightElement: {
                text: BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + item.rightText,
            },
            strikethroughAmount: item.strikethroughAmount,
        });
    });

    if (!isEventCart) {
        billEntries.push({
            id: BILL_ELEMENT_ENTRIES.TOTAL_BILL_AMOUNT_ID,
            leftElement: {
                text: getLeftTextForBillEntry(BILL_ELEMENT_ENTRIES.TOTAL_BILL_AMOUNT_ID),
            },
            rightElement: {
                text: getBillAmount(),
                variant: "V2.body2.neutral.medium",
            },
            isBillDivider: !isTableReservation,
            ...getRedeemableSubText(),
        });
    }

    billEntries.push(...processDiscountList(discountList, isCart));

    if (dineCash && dineCash > 0) {
        const newBillEntries = findBillDivider(billEntries);
        billEntries = newBillEntries?.billEntries;
        const isBillDivider = newBillEntries?.isBillDivider;
        billEntries.push({
            id: BILL_ELEMENT_ENTRIES.DINE_CASH_ID,
            leftElement: { text: BILL_ELEMENT_ENTRIES.DINE_CASH },
            rightElement: {
                text: getFormattedDiscountText(dineCash, String(dineCash)),
                color: TEXT_TYPOGRAPHY.POSITIVE,
                variant: "V2.body2.neutral.medium",
            },
            isBillDivider,
        });
    }
    if (advanceAmount > 0) {
        const newBillEntries = findBillDivider(billEntries);
        billEntries = newBillEntries?.billEntries;
        const isBillDivider = newBillEntries?.isBillDivider;
        billEntries.push({
            id: BILL_ELEMENT_ENTRIES.ADVANCE_AMOUNT_ID,
            leftElement: { text: BILL_ELEMENT_ENTRIES.ADVANCE_AMOUNT },
            rightElement: {
                text: getFormattedDiscountText(advanceAmount, String(advanceAmount)),
                color: TEXT_TYPOGRAPHY.POSITIVE,
                variant: TEXT_TYPOGRAPHY.BODY_B2_MED,
            },
            isBillDivider,
        });
    }

    if (feeObject) {
        const newBillEntries = findBillDivider(billEntries);
        billEntries = newBillEntries?.billEntries;
        const isBillDivider = newBillEntries?.isBillDivider;
        billEntries.push({
            id: BILL_ELEMENT_ENTRIES.CONVENIENCE_FEE_TYPE,
            leftElement: {
                text: feeObject?.ctx?.displayName,
                toolTipTitle: feeObject?.ctx?.displayName,
                toolTipText: feeObject?.ctx?.description,
                isClickable: !isEmpty(feeObject?.ctx?.displayName) && !isEmpty(feeObject?.ctx?.description),
            },
            rightElement: {
                text: BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + feeObject?.value,
                variant: "V2.body2.neutral.medium",
            },
            isBillDivider,
        });
    }

    if (!isEmpty(gst)) {
        const newBillEntries = findBillDivider(billEntries);
        billEntries = newBillEntries?.billEntries;
        const isBillDivider = newBillEntries?.isBillDivider;
        billEntries.push({
            id: BILL_ELEMENT_ENTRIES.GST_ID,
            leftElement: {
                text: BILL_ELEMENT_ENTRIES.GST_CONVENIENCE_FEE,
            },
            rightElement: {
                text: BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + gst,
                variant: "V2.body2.neutral.medium",
            },
            isBillDivider,
        });
    }

    if (subscription?.planId) {
        const newBillEntries = findBillDivider(billEntries);
        billEntries = newBillEntries?.billEntries;
        const isBillDivider = newBillEntries?.isBillDivider;
        billEntries.push({
            id: BILL_ELEMENT_ENTRIES.PLAN_AMOUNT_ID,
            leftElement: {
                text: subscription?.name ? subscription.name : subscription?.planName,
            },
            rightElement: {
                text: BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + subscription?.finalPrice,
                variant: "V2.body2.neutral.medium",
            },
            superOffer: true,
            strikethroughAmount: getSubscriptionStrikedAmount(),
            ...getOfferUrlAndStyle(),
            isBillDivider: isBillDivider,
        });
    }

    if (tip && !isTableReservation && !isEventCart && !isStealStoreOrder) {
        billEntries.push({
            id: isCart ? BILL_ELEMENT_ENTRIES.ADD_TIP_CART_ID : BILL_ELEMENT_ENTRIES.ADD_TIP_ID,
            leftElement: {
                text: "Tip",
            },
            rightElement: {
                text: BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + tip,
                variant: "V2.body2.neutral.medium",
            },
            isBillDivider: true,
        });
    }

    billEntries.push({
        id: BILL_ELEMENT_ENTRIES.BILL_TO_PAY_ID,
        leftElement: {
            text: getLeftTextForBillEntry(BILL_ELEMENT_ENTRIES.BILL_TO_PAY_ID),
            variant: "V2.body2.neutral.bold",
            color: "text_High_Emphasis",
        },
        rightElement: {
            text: BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + currencyFormat(toPayAmount),
        },
    });

    // Add partial payment entries if applicable
    if (partialPaymentDetails?.isPartialPayment) {
        billEntries.push({
            id: BILL_ELEMENT_ENTRIES.ADVANCE_PAID_ID,
            leftElement: {
                text: BILL_ELEMENT_ENTRIES.ADVANCE_PAID,
                variant: "V2.body2.neutral.bold",
                color: "text_High_Emphasis",
            },
            rightElement: {
                text: BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + currencyFormat(partialPaymentDetails.advancePaid),
                variant: "V2.body2.neutral.bold",
            },
            isBillDivider: true,
        });

        billEntries.push({
            id: BILL_ELEMENT_ENTRIES.REMAINING_ID,
            leftElement: {
                text: BILL_ELEMENT_ENTRIES.REMAINING,
                variant: "V2.body2.neutral.regular",
                color: "text_High_Emphasis",
            },
            rightElement: {
                text: BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + currencyFormat(partialPaymentDetails.remainingAmount),
                variant: "V2.body2.neutral.regular",
            },
        });
    }

    return billEntries;
};

export const getCarouselConfig = (item: CollectionProps, style: Style) => {
    return {
        ...getWidthHeightStyles({ width: CAROUSEL_DIMENTIONS.width, height: CAROUSEL_DIMENTIONS.height }, style),
        cloudinaryId: item.imageId,
        href: item.action?.link || "",
        ariaLabel: item.accessibility?.altText || "",
    };
};

export function getGridLayout<T>(layout: GridLayout, collections: T[]): T[][] {
    const rows: T[][] = [];
    const collectionLength = Number(collections?.length) || 0;
    const requiredRows = Number(layout?.rows) || 0;

    if (requiredRows === 0 || collectionLength === 0) {
        return rows;
    }
    const ceil = Math.ceil(collectionLength / requiredRows);

    for (let chunk = 0; chunk < requiredRows; chunk++) {
        rows.push(collections.slice(ceil * chunk, ceil * chunk + ceil));
    }

    return rows;
}

export const getDlsSpacing = (spacingInPX: number, spacing: ISpacing) => {
    // Convert spacing values to numbers for comparison
    const spacingValues = Object.entries(spacing).reduce(
        (acc, [key, value]) => {
            acc[key] = parseInt(value);
            return acc;
        },
        {} as { [key: string]: number },
    );

    const sortedKeys = Object.keys(spacingValues).sort((a, b) => spacingValues[a] - spacingValues[b]);

    let nearestValue = sortedKeys[0];
    for (const key of sortedKeys) {
        if (Math.abs(spacingValues[key] - spacingInPX) < Math.abs(spacingValues[nearestValue] - spacingInPX)) {
            nearestValue = key;
        }
    }
    return nearestValue === "empty" ? "xSmall" : nearestValue;
};
export const getGridConfig = (layout: GridLayout, spacing: ISpacing) => {
    return {
        textVarient: "V2.heading2.neutral.black",
        titleContentSpacing: "xSmall",
        itemSpacing: getDlsSpacing(Number(layout.itemSpacing), spacing),
    };
};
